import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import cn from "classnames";
import { Download, Eye, Loader2, MoreVertical, Pencil, X } from "lucide-react";
import useDesignManagement, {
  DesignSource,
  DesignStatus,
  type Design,
} from "pages/creative/pageV2/useDesignManagement";
import { useDownloadDesign } from "pages/creative/pageV2/utils/useDownloadDesign";
import type { DesignPdfLinks } from "pages/creative/pageV2/utils/downloadDesign";
import { renderPdfPages, type DesignPreviewPage } from "campaign-builder/design/useDesignUpload";
import { Skeleton } from "campaign-builder/ui";
import { getFlyerFormatMeta } from "./formatMeta";
import { DesignFormatFrame } from "./DesignAspectPreview";

// Generalized from the ADM gallery grid: design-format compliance is scoped to the
// channel's allowed formats (passed in) rather than the hardcoded ADM postcard set,
// and labels/previews use the shared flyer format meta.

const GalleryLoadingSkeleton = () => (
  <div className="grid grid-cols-2 gap-3">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="border-border flex flex-col gap-3 rounded-lg border border-solid p-4">
        <Skeleton className="h-[135px] w-full rounded-md" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

const PAGE_SIZE = 12;

interface DesignGalleryGridProps {
  clientId: string;
  format: string;
  /** Formats valid for the current channel + country; designs outside it are non-selectable. */
  allowedFormats: string[];
  activeDesignId?: string | null;
  templateUrl?: string;
  templateLabel?: string;
  onSelect: (design: Design) => void;
  onEdit: (design: Design) => void;
}

// A design without a stored PDF can still be downloaded when the flyer service can render it from its HTML.
const canGeneratePdf = (design: Design) => !!design.hasHtmlContent || design.designSource === DesignSource.FIGMA;

const PreviewModal = ({
  design,
  onClose,
  onDownload,
}: {
  design: Design;
  onClose: () => void;
  onDownload: (design: Design) => Promise<unknown>;
}) => {
  const [downloading, setDownloading] = useState(false);
  const [pages, setPages] = useState<DesignPreviewPage[]>([]);
  const [loading, setLoading] = useState(true);
  const meta = getFlyerFormatMeta(design.format);

  useEffect(() => {
    const src = design.s3Link;
    if (!src) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    renderPdfPages(src)
      .then((rendered) => {
        if (!cancelled) {
          setPages(rendered);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [design.s3Link]);

  const createdAt =
    design.dateCreated instanceof Date && !Number.isNaN(design.dateCreated.getTime())
      ? design.dateCreated.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-cypress-id="design-gallery-preview-modal"
        className="w-[720px] max-w-[92vw] rounded-xl bg-card p-6 shadow-[0px_10px_30px_rgba(2,6,23,0.18)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div className="flex flex-col">
            <span className="text-[20px] font-semibold text-foreground">Preview file</span>
            <span className="text-muted-foreground text-sm">
              {meta?.label ?? design.format}
              {design.designer ? ` · Uploaded by ${design.designer}` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-cypress-id="design-gallery-preview-close"
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="text-muted-foreground flex h-[280px] items-center justify-center text-sm">
            Loading preview…
          </div>
        ) : pages.length ? (
          <div className="flex gap-4">
            {pages.map((page, index) => (
              <div key={page.pageNumber} className="flex flex-1 flex-col gap-2">
                <DesignFormatFrame dataUrl={page.dataUrl} format={design.format} alt={index === 0 ? "Front" : "Back"} />
                <span className="text-muted-foreground text-xs">{index === 0 ? "Front" : "Back"}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-slate-400 flex h-[280px] items-center justify-center text-sm">Preview unavailable</div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <span className="text-muted-foreground text-xs">{createdAt ? `Created at: ${createdAt}` : ""}</span>
          {design.s3Link && (
            <a
              href={design.s3Link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-medium text-foreground underline"
            >
              <Download className="h-4 w-4" />
              Download
            </a>
          )}
          {!design.s3Link && canGeneratePdf(design) && (
            <button
              type="button"
              data-cypress-id="design-gallery-preview-download"
              disabled={downloading}
              aria-busy={downloading}
              onClick={() => {
                setDownloading(true);
                onDownload(design).finally(() => setDownloading(false));
              }}
              className="flex items-center gap-1.5 text-sm font-medium text-foreground underline disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {downloading ? "Preparing…" : "Download"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ItemMenu = ({
  onPreview,
  onDownload,
  onEdit,
}: {
  onPreview: () => void;
  onDownload: () => void;
  onEdit: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const run = (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    action();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Design options"
        data-cypress-id="design-gallery-item-menu"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        className="text-muted-foreground hover:text-slate-700 dark:hover:text-slate-300 flex h-5 w-5 items-center justify-center"
      >
        <MoreVertical className="h-[18px] w-[18px]" />
      </button>
      {open && (
        <div className="border-border absolute right-0 top-6 z-10 w-36 overflow-hidden rounded-lg border border-solid bg-card py-1 shadow-[0px_4px_12px_rgba(2,6,23,0.12)]">
          <button
            type="button"
            onClick={run(onPreview)}
            data-cypress-id="design-gallery-item-preview"
            className="text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-sm"
          >
            <Eye className="h-4 w-4" /> Preview
          </button>
          <button
            type="button"
            onClick={run(onDownload)}
            data-cypress-id="design-gallery-item-download"
            className="text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-sm"
          >
            <Download className="h-4 w-4" /> Download
          </button>
          <button
            type="button"
            onClick={run(onEdit)}
            data-cypress-id="design-gallery-item-edit"
            className="text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-muted flex w-full items-center gap-2 px-3 py-2 text-sm"
          >
            <Pencil className="h-4 w-4" /> Edit
          </button>
        </div>
      )}
    </div>
  );
};

export const DesignGalleryGrid: React.FC<DesignGalleryGridProps> = ({
  clientId,
  format,
  allowedFormats,
  activeDesignId,
  templateUrl,
  templateLabel,
  onSelect,
  onEdit,
}) => {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { designs, setDesigns, loading, paginationInfo } = useDesignManagement({
    clientId,
    pagination: { page: 1, limit },
  });
  const [previewDesign, setPreviewDesign] = useState<Design | null>(null);
  // Keep generated links locally so the next Download opens the stored PDF directly.
  const storeGeneratedLinks = useCallback(
    (designId: string, links: DesignPdfLinks) => {
      const update = { s3Link: links.s3Link ?? undefined, s3CmykLink: links.s3CmykLink ?? undefined };
      setDesigns((prev) => prev.map((design) => (design.id === designId ? { ...design, ...update } : design)));
      setPreviewDesign((prev) => (prev?.id === designId ? { ...prev, ...update } : prev));
    },
    [setDesigns]
  );
  // s3Link first, as before, so uploaded designs keep downloading the file they uploaded.
  const downloadDesign = useDownloadDesign({
    prefer: "rgb",
    features: "noopener,noreferrer",
    onGenerated: storeGeneratedLinks,
  });

  const isCompliantFormat = useCallback(
    (value?: string) => !!value && allowedFormats.includes(value),
    [allowedFormats]
  );

  const visible = useMemo(
    () =>
      designs
        .filter((design) => design.status === DesignStatus.ACTIVE)
        .sort((a, b) => {
          const aCompliant = isCompliantFormat(a.format);
          const bCompliant = isCompliantFormat(b.format);
          if (aCompliant !== bCompliant) return aCompliant ? -1 : 1;
          return Number(b.format === format) - Number(a.format === format);
        }),
    [designs, format, isCompliantFormat]
  );
  const canLoadMore = !!paginationInfo?.hasNextPage;
  const isInitialLoading = loading && designs.length === 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-foreground text-sm font-medium leading-5">Select from gallery</p>
        {templateUrl && (
          <a
            href={templateUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-cypress-id="design-gallery-download-template"
            className="flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-foreground underline"
          >
            <Download className="h-3.5 w-3.5" />
            Download {templateLabel} template
          </a>
        )}
      </div>

      {isInitialLoading ? (
        <GalleryLoadingSkeleton />
      ) : visible.length === 0 ? (
        <div
          data-cypress-id="design-gallery-empty"
          className="border-border flex flex-col items-center gap-1 rounded-lg border border-dashed py-10 text-center"
        >
          <p className="text-foreground text-sm font-medium">No designs yet</p>
          <p className="text-muted-foreground text-xs">Upload a design or create a new one to get started.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            {visible.map((design) => {
              const isSelected = !!activeDesignId && design.id === activeDesignId;
              const meta = getFlyerFormatMeta(design.format);
              const isCompliant = isCompliantFormat(design.format);
              return (
                <div
                  key={design.id}
                  role="button"
                  tabIndex={isCompliant ? 0 : -1}
                  aria-disabled={!isCompliant}
                  data-cypress-id={`design-gallery-item-${design.id}`}
                  onClick={() => isCompliant && onSelect(design)}
                  onKeyDown={(event) => {
                    if (isCompliant && (event.key === "Enter" || event.key === " ")) {
                      event.preventDefault();
                      onSelect(design);
                    }
                  }}
                  className={cn(
                    "flex flex-col gap-3 rounded-lg border-solid p-4 text-left",
                    isCompliant ? "cursor-pointer" : "cursor-not-allowed",
                    isSelected ? "border-2 border-[#222336] dark:border-slate-300" : "border border-border"
                  )}
                >
                  <div className="relative w-full">
                    <div
                      className={cn(
                        "border-border flex h-[135px] items-center justify-center overflow-hidden rounded-md border border-solid bg-muted",
                        !isCompliant && "opacity-50"
                      )}
                    >
                      {design.thumbnailUrl ? (
                        <img src={design.thumbnailUrl} alt={design.designName} className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-slate-400 text-sm">No preview available</span>
                      )}
                    </div>
                    {!isCompliant && (
                      <span className="absolute left-1/2 top-2 -translate-x-1/2 rounded-md bg-[#1e293b]/90 px-2 py-1 text-center text-[11px] font-medium text-white">
                        Artwork not compliant with the channel selected
                      </span>
                    )}
                  </div>
                  <div className="flex w-full items-start gap-3">
                    <div className="flex flex-1 flex-col gap-1 overflow-hidden">
                      <span className="text-foreground truncate text-sm font-medium leading-5">
                        {design.designName}
                      </span>
                      <span className="text-muted-foreground truncate text-xs leading-4">
                        {meta?.label ?? design.format}
                      </span>
                    </div>
                    <ItemMenu
                      onPreview={() => setPreviewDesign(design)}
                      onDownload={() => void downloadDesign(design)}
                      onEdit={() => onEdit(design)}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {canLoadMore && (
            <div className="flex justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => setLimit((current) => current + PAGE_SIZE)}
                data-cypress-id="design-gallery-load-more"
                className="text-foreground border-border flex h-9 items-center gap-2 rounded-lg border border-solid bg-background px-4 text-sm font-medium disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {previewDesign && (
        <PreviewModal design={previewDesign} onClose={() => setPreviewDesign(null)} onDownload={downloadDesign} />
      )}
    </div>
  );
};
